/**
 * 역할(Role) 상수 및 유틸리티 함수
 *
 * V39: 프론트엔드 전역 역할 한글 라벨 통일
 * - 대장님 정책 기준 한글 라벨 매핑
 * - 직원 부여 가능한 역할 목록
 * - 역할 표시 유틸 함수
 */

/**
 * 역할 ID → 한글 라벨 매핑
 *
 * 대장님 정책(확정):
 * - role_te: 일반
 * - role_tl: TL
 * - role_manager: 조직장
 * - role_hr: 인사팀
 * - role_exec: 임원
 * - system_admin: 시스템 관리자 (UI 숨김)
 */
export const ROLE_LABEL_MAP: Record<string, string> = {
  // ========================================
  // 신규 역할 (Active)
  // ========================================
  role_te: '일반',
  role_tl: 'TL',
  role_manager: '조직장',
  role_hr: '인사팀',
  role_exec: '임원',
  system_admin: '시스템 관리자',

  // ========================================
  // 레거시 역할 (하위 호환)
  // ========================================
  super_admin: '시스템 관리자 (구버전)',
  hr_admin: '인사 관리자 (구버전)',
  team_leader: '팀장 (구버전)',
  employee: '일반 직원 (구버전)',
  general: '일반 직원 (구버전)',
  tl: 'TL (구버전)',
  org_manager: '조직장 (구버전)',
  hr: '인사관리자 (구버전)',
  approver: '승인자 (구버전)',
  executive: '임원 (구버전)',
};

/**
 * 직원 생성/수정 시 선택 가능한 역할 ID 목록
 *
 * system_admin 및 레거시 역할은 제외
 */
export const ASSIGNABLE_ROLE_IDS: string[] = [
  'role_te',
  'role_tl',
  'role_manager',
  'role_hr',
  'role_exec',
];

/**
 * V64.4: 조직장/임원 역할 ID 목록
 *
 * 상위 조직장 선택 모달 등에서 "조직장 권한 보유자" 필터에 사용.
 * - role_manager: 조직장 (현행)
 * - org_manager: 조직장 (구버전, 레거시 호환)
 * - role_exec: 임원
 *
 * SSoT 근거:
 * - OrgManagementAccessGuard.tsx: role_manager + role_exec 로 조직관리 접근 판정
 * - ROLE_LABEL_MAP: role_manager='조직장', role_exec='임원', org_manager='조직장 (구버전)'
 */
export const ORG_LEADER_ROLE_IDS: string[] = [
  'role_manager',
  'org_manager',
  'role_exec',
];

/**
 * 직원 부여 가능 여부 확인
 *
 * @param roleId - 역할 ID
 * @returns 부여 가능하면 true
 *
 * @example
 * isAssignableRole('role_hr') // true
 * isAssignableRole('system_admin') // false
 */
export function isAssignableRole(roleId: string): boolean {
  return ASSIGNABLE_ROLE_IDS.includes(roleId);
}

/**
 * 역할 ID의 한글 라벨 조회
 *
 * @param roleId - 역할 ID
 * @param fallback - roleId를 찾지 못했을 때 기본값 (기본: roleId 그대로)
 * @returns 한글 라벨
 *
 * @example
 * getRoleLabel('role_hr') // '인사팀'
 * getRoleLabel('unknown_role') // 'unknown_role'
 * getRoleLabel('unknown_role', '알 수 없음') // '알 수 없음'
 */
export function getRoleLabel(roleId: string, fallback?: string): string {
  return ROLE_LABEL_MAP[roleId] || fallback || roleId;
}

/**
 * 여러 역할 ID의 한글 라벨 조회 (콤마 구분)
 *
 * @param roleIds - 역할 ID 배열
 * @returns 한글 라벨들을 콤마로 구분한 문자열
 *
 * @example
 * getRoleLabels(['role_te', 'role_hr']) // '일반, 인사팀'
 * getRoleLabels([]) // '(없음)'
 * getRoleLabels(undefined) // '(없음)'
 */
export function getRoleLabels(roleIds: string[] | undefined): string {
  if (!roleIds || roleIds.length === 0) {
    return '(없음)';
  }
  return roleIds.map(id => getRoleLabel(id)).join(', ');
}
